import "@atlas/composite/styles.css";
import { ToastProvider } from "@atlas/core";
import {
  createAppQueryClient,
  getSessionData,
  TranslationProvider,
} from "@atlas/sdk";
import { useUserInfo } from "@query";
import { APP_BASE_PATH } from "@shared";
import { QueryClientProvider } from "@tanstack/react-query";
import * as locales from "@translations/locales.json";
import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router";
import App from "./app/App";
import "./app/index.css";
import { clearClientAuthSession, client, hasAuthToken } from "@atlas/sdk";

let isReloading = false;

// Add interceptor to handle 401 errors and reload the page if the user is not authenticated
client.interceptors.response.use(
  (response) => response,
  (error) => {
    const isAuthEndpoint = error.config?.url?.includes("/auth/login");
    if (
      error.response?.status === 401 &&
      !isReloading &&
      !isAuthEndpoint &&
      hasAuthToken()
    ) {
      isReloading = true;
      clearClientAuthSession();
      window.dispatchEvent(new CustomEvent("session-expired"));
    }
    return Promise.reject(error);
  },
);

const queryClient = createAppQueryClient({
  getHeaders: () => {
    const { companyId, userId, token } = getSessionData();

    return {
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
      companyId: companyId ? String(companyId) : "",
      userId: userId ?? "",
      "X-Request-ID": crypto.randomUUID(),
    };
  },
  defaultOptions: {
    queries: {
      refetchOnMount: true,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000,
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error(
    "Failed to find the root element. Ensure index.html has <div id='root'></div>",
  );
}

function RootApp() {
  const { data: userInfo } = useUserInfo(APP_BASE_PATH);
  const language = userInfo?.language || "en-US";

  return (
    <TranslationProvider
      defaultTranslations={locales}
      language={language}
      fallbackLng="en-US">
      <ToastProvider position="top-center">
        <BrowserRouter basename={APP_BASE_PATH}>
          <App />
        </BrowserRouter>
      </ToastProvider>
    </TranslationProvider>
  );
}

const root = ReactDOM.createRoot(rootElement as HTMLElement);

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <RootApp />
    </QueryClientProvider>
  </React.StrictMode>,
);
